import { createFileRoute } from "@tanstack/react-router";
import { ResourceArticle } from "@/components/ResourceArticle";
import { buildBreadcrumbs, buildArticle } from "@/components/SchemaJsonLd";
import heroImg from "@/assets/garage-floor-dc.jpg";

export const Route = createFileRoute("/blog/garage-renovation-tips")({
  head: () => ({
    meta: [
      { title: "Garage Renovation Tips From Calgary Region Pros | MLC Epoxy Floors" },
      { name: "description", content: "Storage, lighting, and flooring decisions that pay off long-term — practical garage renovation advice from coating specialists working across Calgary and surrounding areas." },
      { property: "og:title", content: "Garage Renovation Tips From Calgary Region Pros" },
      { property: "og:description", content: "Storage, lighting, and flooring decisions that pay off long-term." },
      { property: "og:url", content: "/blog/garage-renovation-tips" },
      { property: "og:image", content: heroImg },
      { property: "og:type", content: "article" },
    ],
    links: [{ rel: "canonical", href: "/blog/garage-renovation-tips" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(buildArticle({
          title: "Garage Renovation Tips From Calgary Region Pros",
          description: "Storage, lighting, and flooring decisions that pay off long-term — practical garage renovation advice from coating specialists working across Calgary and surrounding areas.",
          slug: "garage-renovation-tips",
          datePublished: "2026-02-14",
        })),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(buildBreadcrumbs([
          { name: "Home", href: "/" },
          { name: "Blog", href: "/blog" },
          { name: "Garage Renovation Tips", href: "/blog/garage-renovation-tips" },
        ])),
      },
    ],
  }),
  component: () => (
    <ResourceArticle
      eyebrow="Feb 14, 2026"
      title="Garage Renovation Tips From Calgary Region Pros"
      intro="We coat 200+ garages a year across the Calgary region — and we see every renovation decision that aged well, and every one that didn't. Here's what's worth your money."
      image={heroImg}
      backHref="/blog"
      backLabel="All Articles"
      sections={[
        {
          type: "list",
          heading: "Decisions That Pay Off",
          items: [
            { label: "Floor first, everything else after", desc: "Cabinets, slatwall, and lifts make floor coating nearly impossible to do well later. Coat the floor before installing anything wall-mounted." },
            { label: "Overhead LED panels, not single bulbs", desc: "Two 4-ft LED panels per bay eliminates shadows and makes the space usable for detail work or mechanic projects." },
            { label: "20-amp dedicated outlets", desc: "If you'll ever run a compressor, EV charger, or welder, retrofit costs 3× more than during a renovation." },
            { label: "Insulated garage door", desc: "Cuts heating/cooling load by 30% in finished garages. Pays back within 5 years in the Calgary region climate." },
            { label: "Slatwall over pegboard", desc: "Holds 4× the weight, looks better, and configures in minutes. Pegboard is a false economy." },
          ],
        },
        {
          type: "list",
          heading: "Decisions That Don't",
          items: [
            { label: "Mini-split HVAC before insulating", desc: "Conditioned air leaks out through uninsulated walls and ceilings. Insulate first, condition second." },
            { label: "DIY epoxy kits", desc: "Discussed at length in our cost guide — they fail in 2–3 years and removal eats the savings." },
            { label: "Premium cabinets in a cold garage", desc: "Particleboard cabinets swell with humidity. Either condition the space or choose marine-grade plywood or metal." },
            { label: "Rubber tile flooring", desc: "Looks great year one. By year three, edges curl and trap moisture against the concrete." },
          ],
        },
        {
          type: "prose",
          heading: "Order of Operations",
          body: "If you're planning a full garage renovation, here's the sequence that works: structural and electrical first, then insulation and drywall, then floor coating, then cabinets and storage, then lighting and finish work. Skipping or reordering steps almost always costs more in the end.",
        },
        {
          type: "links",
          heading: "Related On This Site",
          intro: "Start with the floor — here's where to go next.",
          links: [
            { href: "/services/flake-epoxy-flooring", label: "Flake Epoxy Flooring", desc: "The most popular renovation-friendly coating in the Calgary region." },
            { href: "/services/polyaspartic-floor-coatings", label: "One-Day Polyaspartic Coatings", desc: "Walk on it in hours — ideal for active renovations." },
            { href: "/gallery?category=Residential", label: "Residential Garage Gallery", desc: "Renovation-stage and finished garage projects." },
          ],
        },
      ]}
    />
  ),
});
